import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getNurses, getUnit, setNurseUnit } from '../../api/dashboard';
import { errorCode } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import StatusMessage from '../../components/StatusMessage';
import { adminErrorMessage } from './adminErrors';
import NurseTable from './NurseTable';
import './Admin.css';

// The picker for adding nurses, like adding students to a class in the
// nursing-school dashboard: a search box and a list to click.
function AssignPanel({ nurses, onPick }) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const matches = nurses.filter((n) =>
    !q || (n.name || '').toLowerCase().includes(q) || (n.email || '').toLowerCase().includes(q));
  return (
    <div className="assign">
      <input
        type="search"
        className="search-input"
        placeholder="Search nurses to add…"
        aria-label="Search nurses to add"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus
      />
      {matches.length === 0 ? (
        <p className="admin-hint">{nurses.length === 0 ? 'Every nurse is already on this unit.' : 'No nurses match your search.'}</p>
      ) : (
        <div className="assign-panel">
          {matches.map((n) => (
            <button key={n.id} type="button" className="assign-panel-row" onClick={() => onPick(n)}>
              <span>{n.name || n.email}</span>
              <span className="assign-panel-meta">{n.unit ? `Now on ${n.unit.name}` : 'No unit'}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const UnitDetail = () => {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [unit, setUnit] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [allNurses, setAllNurses] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rosterError, setRosterError] = useState('');

  const load = useCallback(async () => {
    setError(null);
    setUnit(null);
    try {
      setUnit(await getUnit(unitId));
    } catch (err) {
      setError(errorCode(err) === 'not_found' ? 'not_found' : adminErrorMessage(err, "We couldn't load this unit."));
    }
  }, [unitId]);

  useEffect(() => {
    load();
  }, [load]);

  // Every nurse in the organization, for the add picker. Loaded when it opens.
  useEffect(() => {
    if (!adding || allNurses) return;
    getNurses()
      .then((res) => setAllNurses(res.nurses || []))
      .catch((err) => { setRosterError(adminErrorMessage(err, "We couldn't load the nurses.")); setAdding(false); });
  }, [adding, allNurses]);

  // A nurse is on one unit, so adding one here moves them off any other unit.
  const assign = async (nurse, toUnitId) => {
    setBusyId(nurse.id);
    setRosterError('');
    try {
      const updated = await setNurseUnit(nurse.id, toUnitId);
      setAllNurses((prev) => prev && prev.map((n) => (n.id === updated.id ? updated : n)));
      setUnit((prev) => ({
        ...prev,
        nurses: toUnitId
          ? [...prev.nurses.filter((n) => n.id !== updated.id), updated]
          : prev.nurses.filter((n) => n.id !== updated.id),
      }));
    } catch (err) {
      setRosterError(adminErrorMessage(err, "Could not change this nurse's unit. Please try again."));
    } finally {
      setBusyId(null);
    }
  };

  const back = (
    <button type="button" className="back-button" onClick={() => navigate('/units')}>← Units</button>
  );

  if (error || !unit) {
    return (
      <>
        <PageHeader title="Unit" before={back} />
        <div className="component-container">
          <div className="admin-content">
            {error === 'not_found' && <StatusMessage kind="error">This unit doesn't exist in your organization.</StatusMessage>}
            {error && error !== 'not_found' && <StatusMessage kind="error" onRetry={load}>{error}</StatusMessage>}
            {!error && <StatusMessage kind="loading">Loading unit…</StatusMessage>}
          </div>
        </div>
      </>
    );
  }

  const nurses = [...(unit.nurses || [])].sort((a, b) => (a.name || a.email).localeCompare(b.name || b.email));
  const rooms = [...(unit.rooms || [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

  return (
    <>
      <PageHeader title={unit.name} before={back} />
      <div className="component-container">
        <div className="detail-content">
          <section className="detail-section">
            <div className="detail-section-header">
              <h2 className="section-title">Nurses <span className="count-chip">{nurses.length}</span></h2>
              <button type="button" className="title-button" onClick={() => setAdding((v) => !v)}>
                {adding ? 'Close' : '+ Add Nurse'}
              </button>
            </div>
            {rosterError && <span className="field-error" role="alert">{rosterError}</span>}
            {adding && (
              allNurses
                ? <AssignPanel nurses={allNurses.filter((n) => n.id && n.unit?.id !== unit.id)} onPick={(n) => assign(n, unit.id)} />
                : <p className="admin-loading">Loading nurses…</p>
            )}
            <NurseTable
              nurses={nurses}
              emptyText="No nurses are assigned to this unit."
              onRemove={(n) => assign(n, null)}
              removingId={busyId}
            />
          </section>

          <section className="detail-section">
            <h2 className="section-title">Rooms <span className="count-chip">{rooms.length}</span></h2>
            {rooms.length === 0 ? (
              <p className="admin-hint">No rooms are assigned to this unit.</p>
            ) : (
              <div className="room-chips">
                {rooms.map((room) => <span key={room} className="unit-chip">{room}</span>)}
              </div>
            )}
          </section>

          <p className="admin-hint">Rooms are managed by Aren. Contact Aren to change them.</p>
        </div>
      </div>
    </>
  );
};

export default UnitDetail;
